/* External */
import { Map, isCollection } from 'immutable';

/* Ott-common */
import { createImageResizer } from '@dtvgo/image-resizers';

/* Utils */
import { sizeByViewport } from './cardsUtils';

const imageResizer = createImageResizer();

/**
 * @typedef Image
 * @type {object}
 * @property {string} ratio
 * @property {string} url
 * @property {string} type
 * @property {number} width
 * @property {number} height
 */

/**
 * @method getImage
 * @param {import('immutable').Map<string, Image>} images
 * @param {number} imageSize
 * @param {string} aspectRatio
 */
export const getImage = (
  images,
  imageSize = 0,
  aspectRatio = '',
) => {
  let imagesToUse = images;
  let image = imagesToUse;
  if (Array.isArray(imagesToUse)) {
    imagesToUse = Map(imagesToUse
      .filter(Boolean)
      .map((item, index) => [item?.ratio || `index-${index}`, item]));
  } else if (!isCollection(imagesToUse)) {
    imagesToUse = Map(imagesToUse);
  }
  if (imagesToUse?.size > 0) {
    image = imagesToUse?.get(aspectRatio)
      // Find closest ratio
      || imagesToUse
        ?.filter(({ ratio }) => !!ratio)
        ?.minBy((a) => {
          let targetProportion = getProportion(aspectRatio);
          let imageProportion = getProportion(a.ratio);

          if (targetProportion > 1) {
            targetProportion = 1 / targetProportion;
            imageProportion = 1 / imageProportion;
          }

          return Math.abs(targetProportion - imageProportion);
        })
      // Try unknown ratio if there is no image with ratio
      || imagesToUse?.get('unknown')
      // If no unknown ratio return the first image
      || imagesToUse?.first();
  }

  return resizeImage(image?.url, imageSize, image?.ratio || aspectRatio);
};

export function resizeImage(imageUrl, imageSize = 0, aspectRatio = undefined) {
  const width = sizeByBreakpoint(sizeByViewport(imageSize));
  return imageResizer.getImageUrl(imageUrl, {
    width,
    height: Math.ceil(getImageDimensionsByWidth(width, aspectRatio)),
  });
}

function getProportion(aspectRatio) {
  const [width, height] = aspectRatio.split(':');
  return width / height;
}

/**
 * @method getImageDimensionsByWidth
 * @param {number} imageWidth
 * @param {string} ratio
 */
function getImageDimensionsByWidth(imageWidth, ratio) {
  if (ratio) {
    const [width, height] = ratio.split(':');
    return imageWidth * (height / width);
  }

  return imageWidth;
}

/**
 * @method pixelRatio
 * @param {number} size
 */
function pixelRatio(size) {
  let scale = 1;
  if (window.devicePixelRatio) scale = window.devicePixelRatio;
  return size * scale;
}

/**
 * @method sizeByBreakpoint
 * @param {number} size
 */
function sizeByBreakpoint(size) {
  const pageWidth = document.body.clientWidth;
  const proportion = size / pageWidth;
  const roundValue = pixelRatio(100);
  const resize = (breakpoint) => proportion * breakpoint;
  const resized = pageWidth >= 1920 ? resize(1920) : resize(1280);

  return Math.floor(Math.ceil(Math.floor(resized) / roundValue) * roundValue);
}
